'use client';

import { ToggleGroup } from '../../../../components/toggle-group';
import { useTranslations } from 'next-intl';
import {
  isWebAppClusterDecision,
  type WebAppClusterDecision,
} from './detect-web-apps-confirm';

const DECISION_VALUES = ['create', 'link', 'skip'] as const;

type WebAppClusterDecisionGroupProps = {
  value: WebAppClusterDecision;
  onValueChange: (value: WebAppClusterDecision) => void;
};

export function WebAppClusterDecisionGroup({
  value,
  onValueChange,
}: WebAppClusterDecisionGroupProps) {
  const t = useTranslations(
    'designSystem.networkSettings.externalInventory.webApp',
  );
  const tShared = useTranslations(
    'designSystem.networkSettings.externalInventory.shared',
  );
  const labels = {
    create: t('createDraft'),
    link: tShared('link'),
    skip: tShared('skip'),
  };

  return (
    <ToggleGroup.Root
      value={[value]}
      onValueChange={({ value: next }) => {
        const selected = next[0];

        if (selected && isWebAppClusterDecision(selected)) {
          onValueChange(selected);
        }
      }}
      onClick={(event) => {
        event.stopPropagation();
      }}
      variant="ghost"
      size="sm"
      w="full"
      gap={0}
      p="0.5"
      borderWidth="0"
      borderRadius="full"
      bg="bg.neutral.subtle"
      _horizontal={{ divideX: '0' }}
    >
      {DECISION_VALUES.map((decision) => (
        <ToggleGroup.Item
          key={decision}
          value={decision}
          aria-label={labels[decision]}
          flex={1}
          h="8"
          px="3"
          minW={0}
          borderWidth="0"
          borderRadius="full"
          fontSize="sm"
          fontWeight="medium"
          lineHeight="5"
          justifyContent="center"
          color="fg.neutral.muted"
          bg="transparent"
          shadow="none"
          boxShadow="none"
          _hover={{
            bg: 'transparent',
            color: 'fg.neutral.muted',
          }}
          _on={{
            bg: 'bg.neutral.muted',
            color: 'fg.neutral.default',
            shadow: 'sm',
            _hover: {
              bg: 'bg.neutral.muted',
              color: 'fg.neutral.default',
            },
          }}
        >
          {labels[decision]}
        </ToggleGroup.Item>
      ))}
    </ToggleGroup.Root>
  );
}
